// ✅ src/pages/EmployeeDashboard.js
// Changes made:
// 1) Removed company-scanning fallback in "Fetch employment status"
// 2) Removed getUserDetails() usage (which can scan companies) in the route guard
// 3) All fallbacks now only read: users/{uid}.companyId (UID-first)

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";

import {
  doc,
  getDoc,
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  updateDoc,
} from "firebase/firestore";
import { isPilotActive } from "../utils/pilot";

import LogoutButton from "../components/LogoutButton";
import {
  FaUser,
  FaCalendarAlt,
  FaPlane,
  FaGraduationCap,
  FaTicketAlt,
  FaTasks,
} from "react-icons/fa";
import { IoTimer } from "react-icons/io5";
import { MdTravelExplore } from "react-icons/md";
import { FaHeadset } from "react-icons/fa6";
import { FiBell } from "react-icons/fi";
import Oiiko from "../tools/oiikotranslogo2.png";
import "../styles/EmployeeDashboard.css";

import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/oiiko loading.json";

// Format Firestore Timestamp/Date/string into MM-DD-YYYY
function formatNotifDateMMDDYYYY(value) {
  if (!value) return "";
  const d =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

export default function EmployeeDashboard() {
  const { user, group, loading } = useAuth();
  const navigate = useNavigate();

  const [employeeName, setEmployeeName] = useState("");
  const [planName, setPlanName] = useState("Free");
  const [status, setStatus] = useState("Active");

  const [pilotActive, setPilotActive] = useState(false);

  const [employeeGroup, setEmployeeGroup] = useState("");
  const [helpdeskRole, setHelpdeskRole] = useState("");

  const [employeeInfoLoading, setEmployeeInfoLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [companyAccessOk, setCompanyAccessOk] = useState(true);
  const [companyAccessStatus, setCompanyAccessStatus] = useState("active");

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // ✅ Helper: resolve companyId using UID-first approach (no scanning)
  const resolveCompanyId = async () => {
    if (!user?.uid) return null;

    // 1) Prefer AuthContext
    if (user.companyId) return user.companyId;

    // 2) Fallback: users/{uid}.companyId
    const userSnap = await getDoc(doc(db, "users", user.uid));
    return userSnap.data()?.companyId || null;
  };

  // 🔹 Fetch employee info + plan (SOURCE OF TRUTH = company doc)
  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setEmployeeInfoLoading(false);
        return;
      }

      setEmployeeInfoLoading(true);

      try {
        const companyId = await resolveCompanyId();

        if (!companyId) {
          console.warn("No companyId found for this employee.");
          setPlanName("Free");
          setPilotActive(false);
          setCompanyAccessOk(false);
          setCompanyAccessStatus("no_company");
          setEmployeeName("Employee");
          return;
        }

        // Employee record
        const employeeSnap = await getDoc(
          doc(db, `companies/${companyId}/employees`, user.uid),
        );

        if (employeeSnap.exists()) {
          const emp = employeeSnap.data() || {};
          setEmployeeName(emp.name || "Employee");
          setEmployeeGroup(emp.group || "");
          setHelpdeskRole(emp.helpdeskRole || "");
        } else {
          setEmployeeName("Employee");
        }

        // Company doc (pilot + subscription)
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const company = companySnap.data() || {};

        const activePilot = isPilotActive(company.pilot);
        setPilotActive(activePilot);

        const subStatus = String(
          company?.subscription?.status || "none",
        ).toLowerCase();

        const subscriptionAccessOk = ["free", "active", "trialing"].includes(
          subStatus,
        );

        const accessOk = activePilot || subscriptionAccessOk;
        setCompanyAccessOk(accessOk);
        setCompanyAccessStatus(activePilot ? "pilot" : subStatus);

        if (activePilot) {
          setPlanName("Premium");
          return;
        }

        setPlanName(company?.subscription?.planName || "Free");
      } catch (error) {
        console.error("❌ Error fetching employee/company plan:", error);
        setPlanName("Free");
        setPilotActive(false);
        setCompanyAccessOk(false);
        setCompanyAccessStatus("error");
        setEmployeeName("Employee");
      } finally {
        setEmployeeInfoLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.companyId]);

  // 🔔 Realtime notifications (latest 20)
  useEffect(() => {
    if (!user?.uid) return;

    const qRef = query(
      collection(db, `users/${user.uid}/notifications`),
      orderBy("createdAt", "desc"),
      limit(20),
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const visible = rows.filter((n) => n.archived !== true);

        setNotifications(visible);

        const unread = visible.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
        setUnreadCount(unread);
      },
      (err) => console.error("❌ Notifications snapshot error:", err),
    );

    return () => unsub();
  }, [user?.uid]);

  // ✅ Auto-mark unread as read when panel opens
  useEffect(() => {
    if (!notifOpen || !user?.uid) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;

    Promise.all(
      unread.map((n) =>
        updateDoc(doc(db, `users/${user.uid}/notifications/${n.id}`), {
          read: true,
        }),
      ),
    ).catch((e) => console.error("❌ Auto-mark read failed:", e));
  }, [notifOpen, notifications, user?.uid]);

  // Close panel on outside click
  useEffect(() => {
    if (!notifOpen) return;

    const onDown = (e) => {
      const panel = document.querySelector(".employee-notif-panel");
      const btn = document.querySelector(".employee-bell-btn");
      if (!panel || !btn) return;

      const clickedInside = panel.contains(e.target) || btn.contains(e.target);
      if (!clickedInside) setNotifOpen(false);
    };

    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [notifOpen]);

  // 🔒 Route guard: UID-first (no getUserDetails, no scanning)
  useEffect(() => {
    if (loading) return;
    if (!user?.uid) return;

    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists()) return;

        const details = snap.data() || {};
        if (details.profileCompleted === false) {
          navigate(
            details.selfOnboarding ? "/onboarding" : "/profile-pending",
            {
              replace: true,
            },
          );
        }
      } catch (e) {
        console.error("Guard check failed:", e);
      }
    })();
  }, [user?.uid, loading, navigate]);

  // 🔹 Fetch employment status (UID-first; NO company scanning)
  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setStatusLoading(false);
        return;
      }

      setStatusLoading(true);
      try {
        const companyId = await resolveCompanyId();

        if (!companyId) {
          // If there's no companyId, choose a safe default (or lock view elsewhere)
          setStatus("Active");
          return;
        }

        const snap = await getDoc(
          doc(db, `companies/${companyId}/employees`, user.uid),
        );

        if (snap.exists()) setStatus(snap.data().status || "Active");
      } catch (e) {
        console.error("Error fetching status:", e);
      } finally {
        setStatusLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.companyId]);

  // 🔐 Redirect if terminated (don’t logout; just block access)
  useEffect(() => {
    if (status === "Terminated") {
      navigate("/employee-terminated", { replace: true });
    }
  }, [status, navigate]);

  // Access map
  const baseAccess = {
    Free: ["Profile", "Time Management", "Scheduling"],
    Basic: ["Profile", "Time Management", "Scheduling"],
    Standard: [
      "Profile",
      "Time Management",
      "Leave",
      "Scheduling",
      "Expense",
      "Tasks",
    ],
    Premium: [
      "Profile",
      "Time Management",
      "Leave",
      "Scheduling",
      "Training",
      "Expense",
      "Tickets",
      "Tasks",
    ],
    Training: ["Profile", "Training"],
    Helpdesk: ["Profile", "Tickets"],
  };

  const featureAccess = !companyAccessOk
    ? {
        Free: ["Profile"],
        Basic: ["Profile"],
        Standard: ["Profile"],
        Premium: ["Profile"],
        Training: ["Profile"],
        Helpdesk: ["Profile"],
      }
    : status === "Pending"
    ? {
        Free: ["Profile"],
        Basic: ["Profile"],
        Standard: ["Profile"],
        Premium: ["Profile"],
        Training: ["Profile"],
        Helpdesk: ["Profile"],
      }
    : baseAccess;

  const effectivePlanName = pilotActive ? "Premium" : planName;
  const hasAccess = (feature) =>
    featureAccess[effectivePlanName]?.includes(feature);

  const effectiveGroup = (group ?? employeeGroup ?? "")
    .toString()
    .trim()
    .toUpperCase();

  const isITGroup = effectiveGroup === "IT";
  const hasHelpdeskRole = ["agent", "manager"].includes(
    (helpdeskRole || "")
      .toString()
      .trim()
      .toLowerCase(),
  );

  const isPageLoading = loading || employeeInfoLoading || statusLoading;

  if (isPageLoading) {
    return (
      <div className="admin-loading-screen">
        <Lottie
          animationData={loadingAnimation}
          loop
          autoplay
          className="admin-loading-lottie"
        />
        <p className="admin-loading-text">Loading your dashboard…</p>
      </div>
    );
  }

  function isMobileScreen() {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 520px)").matches;
  }
  const closeNotif = () => setNotifOpen(false);

  const markNotifRead = async (notifId) => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, `users/${user.uid}/notifications/${notifId}`), {
        read: true,
      });
    } catch (e) {
      console.error("❌ markNotifRead failed:", e);
    }
  };

  return (
    <div className="employeedashboard-container">
      <nav className="employeedashboard-nav">
        <div className="nav-left">
          <img src={Oiiko} alt="Oiiko Logo" className="brand-logoemp" />
          <div className="nav-text-block">
            <h1 className="welcome-text">Welcome, {employeeName}</h1>
            <p className="welcome-subtitle">Your workspace at a glance</p>
          </div>
        </div>

        <div className="nav-right">
          <button
            type="button"
            className="employee-nav-btn"
            onClick={() => navigate("/profile")}
            aria-label="My profile"
            title="My Profile"
          >
            <FaUser className="employee-nav-icon" />
          </button>

          <div className="employee-bell-wrap">
            <button
              type="button"
              className="employee-nav-btn"
              onClick={() => setNotifOpen((v) => !v)}
              aria-label="Notifications"
            >
              <FiBell className="employee-bell-icon" />
              {unreadCount > 0 && (
                <span className="employee-bell-badge">{unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <>
                <div
                  className="employee-notif-overlay"
                  onClick={closeNotif}
                  aria-hidden="true"
                />

                <div
                  className={`employee-notif-panel ${
                    isMobileScreen() ? "employee-notif-panel--mobile" : ""
                  }`}
                  role="dialog"
                  aria-label="Notifications"
                >
                  <div className="employee-notif-header">
                    <div className="employee-notif-title">Notifications</div>
                  </div>

                  <div className="employee-notif-list">
                    {notifications.length === 0 ? (
                      <div className="employee-notif-empty">
                        No notifications yet.
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`employee-notif-item ${
                            n.read ? "" : "unread"
                          }`}
                          onClick={() => markNotifRead(n.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              markNotifRead(n.id);
                          }}
                        >
                          <div className="employee-notif-row1">
                            <div className="employee-notif-item-title">
                              {n.title || "Notification"}
                            </div>
                            <div className="employee-notif-item-date">
                              {formatNotifDateMMDDYYYY(n.createdAt) ||
                                n.date ||
                                ""}
                            </div>
                          </div>

                          <div className="employee-notif-item-msg">
                            {n.message || ""}
                          </div>

                          <div className="employee-notif-item-meta">
                            {n.workSiteName ? `Site: ${n.workSiteName}` : ""}
                            {n.type ? ` • ${n.type}` : ""}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <LogoutButton variant="employee" />
        </div>
      </nav>

      <main className="employeedashboard-main">
        <section className="employeedashboard-header-row">
          <h2 className="dashboard-title">Employee Dashboard</h2>
          <p className="dashboard-subtitle">
            Manage your time, schedule, time off, expenses, and learning from a
            single dashboard.
          </p>
        </section>

        {!companyAccessOk && (
          <div
            className="billing-warning-banner banner-bar"
            role="status"
            aria-live="polite"
          >
            Company access is currently unavailable ({companyAccessStatus}). You
            can still view your profile.
          </div>
        )}

        <section className="employeedashboard-icons">
          {hasAccess("Time Management") && (
            <Link to="/time-management" className="employeeicon-link">
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <IoTimer className="icon-style" />
                </span>
                <h3 className="icon-titles">Time & Attendance</h3>
              </div>
            </Link>
          )}

          {hasAccess("Scheduling") && (
            <Link to="/employee-scheduling" className="employeeicon-link">
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <FaCalendarAlt className="icon-style" />
                </span>
                <h3 className="icon-titles">My Schedule</h3>
              </div>
            </Link>
          )}

          {hasAccess("Tasks") && (
            <Link to="/employee-tasks" className="employeeicon-link">
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <FaTasks className="icon-style" />
                </span>
                <h3 className="icon-titles">My Tasks</h3>
              </div>
            </Link>
          )}

          {hasAccess("Leave") && (
            <Link to="/employee-leave" className="employeeicon-link">
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <FaPlane className="icon-style" />
                </span>
                <h3 className="icon-titles">Leave Requests</h3>
              </div>
            </Link>
          )}

          {hasAccess("Expense") && (
            <Link to="/employee-expense" className="employeeicon-link">
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <MdTravelExplore className="icon-style" />
                </span>
                <h3 className="icon-titles">Expenses</h3>
              </div>
            </Link>
          )}

          {hasAccess("Training") && (
            <Link
              to="/employee-training"
              className="employeeicon-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="icon-wrapper">
                <span className="icon-circle">
                  <FaGraduationCap className="icon-style" />
                </span>
                <h3 className="icon-titles">Learning</h3>
              </div>
            </Link>
          )}

          {hasAccess("Tickets") && (
            <>
              {!(isITGroup || hasHelpdeskRole) && (
                <Link to="/employee-submit" className="employeeicon-link">
                  <div className="icon-wrapper">
                    <span className="icon-circle">
                      <FaHeadset className="icon-style" />
                    </span>
                    <h3 className="icon-titles">IT Support</h3>
                  </div>
                </Link>
              )}

              {(isITGroup || hasHelpdeskRole) && (
                <Link
                  to="/submitted-tickets"
                  className="employeeicon-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="icon-wrapper">
                    <span className="icon-circle">
                      <FaTicketAlt className="icon-style" />
                    </span>
                    <h3 className="icon-titles">Help Desk</h3>
                  </div>
                </Link>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
